import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { switchPlan } from '../../apiLayer/vsCodeAuthAPIs/authAPIs';
import { cn } from '../../lib/utils';
import { Wallet, Key, CreditCard, Loader2, ArrowLeftRight, ChevronDown } from 'lucide-react';

const PLAN_CONFIG = {
  fixed: {
    label: 'Fixed Subscription',
    icon: CreditCard,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20'
  },
  byok: {
    label: 'Bring Your Own Key',
    icon: Key,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20'
  },
  sixth_credit: {
    label: 'Sixth Credits (PAYG)',
    icon: Wallet,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20'
  }
};

const PlanSwitcher = ({ currentPlan, onSwitch }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  const current = PLAN_CONFIG[currentPlan] || PLAN_CONFIG.fixed;
  const CurrentIcon = current.icon;

  // Get available plans to switch to (exclude current)
  const availablePlans = Object.entries(PLAN_CONFIG)
    .filter(([key]) => key !== currentPlan)
    .map(([key, config]) => ({ key, ...config }));

  const handleSwitch = async (targetPlan) => {
    setLoading(true);
    setError(null);
    setShowMenu(false);

    try {
      const result = await switchPlan(targetPlan);

      if (result.success) {
        localStorage.setItem('sixth_plan', result.current_plan);

        if (onSwitch) {
          onSwitch(result.current_plan);
        }

        // Always stay on unified dashboard
        window.location.href = '/vscode/dashboard';
      }
    } catch (err) {
      console.error('Error switching plan:', err);
      setError(err.response?.data?.detail || 'Failed to switch plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanClick = (key) => {
    if (key === 'fixed') {
      setShowMenu(false);
      navigate('/pricing');
    } else {
      handleSwitch(key);
    }
  };

  return (
    <div className="w-full relative">
      {/* Current Plan Indicator */}
      <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10 mb-3">
        <div className="flex items-center gap-2">
          <CurrentIcon className={cn("w-4 h-4", current.color)} />
          <span className="text-sm text-white">{current.label}</span>
        </div>
        <span className={cn(
          "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider",
          current.bgColor, current.color
        )}>
          Active
        </span>
      </div>

      {/* Switch Button */}
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={loading}
        className={cn(
          "w-full flex items-center justify-center gap-2 p-3 rounded-lg transition-all text-sm font-medium",
          "bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20",
          "text-muted-foreground hover:text-white",
          loading && "opacity-50 cursor-not-allowed"
        )}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ArrowLeftRight className="w-4 h-4" />
        )}
        Switch Plan
        <ChevronDown className={cn("w-3 h-3 transition-transform", showMenu && "rotate-180")} />
      </button>

      {/* Dropdown Menu */}
      {showMenu && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#121219] border border-white/10 rounded-lg shadow-xl overflow-hidden z-50">
          {availablePlans.map(({ key, label, icon: Icon, color }) => (
            <button
              key={key}
              onClick={() => handlePlanClick(key)}
              disabled={loading}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left"
            >
              <Icon className={cn("w-4 h-4", color)} />
              <span className="text-sm text-white">{label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
          {error}
        </div>
      )}
    </div>
  );
};

export default PlanSwitcher;
